// Factura / estado de cuenta imprimible de un aliado (armería): lista de las
// personas referidas en el mes con lo que se factura por cada una, y el
// resumen (cursos − comisión + penalidades). Pedido del cliente 2026-09-28:
// "un tipo de recibo o factura para los cursos de las armerías... con las
// personas que vienen referidos por ellos". Se usa en el dashboard
// (Aliados → Detalle del mes) y en el portal /armeros (Mis clientes).
import {
  money,
  monthLabel,
  partnerRowStatus,
  partnerBillingOf,
  summarizePartnerRows,
  settlementStatus,
  formatDueDate,
  dueDateFor,
  PARTNER_CHANGE_HOURS,
  DEFAULT_PAYMENT_TERM_DAYS,
} from './partners';
import { courseLanguageLabel, licenseTypeLabel } from './privateCourse';

const esc = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const formatTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const todayLabel = () => new Date().toLocaleDateString('es-PR', { day: 'numeric', month: 'long', year: 'numeric' });

// Número de factura: CÓDIGO-AAAAMM (ej. GUNS-202609)
export const invoiceNumber = (partner, monthKey) => `${(partner && partner.code) || 'ALI'}-${String(monthKey || '').replace('-', '')}`;

export function partnerInvoiceHtml({ partner, monthKey, rows, settlement }) {
  const summary = summarizePartnerRows(rows);
  const termDays = (partner && partner.paymentTermDays) || DEFAULT_PAYMENT_TERM_DAYS;
  const dueDate = (settlement && settlement.dueDate) || dueDateFor(monthKey, termDays);
  const status = settlement ? settlementStatus(settlement) : null;

  const bodyRows = rows.length === 0
    ? `<tr><td colspan="8" class="muted">Sin clientes referidos en ${esc(monthLabel(monthKey))}.</td></tr>`
    : rows.map((r) => {
      const st = partnerRowStatus(r);
      const bill = partnerBillingOf(r);
      const isPenalty = bill.kind === 'no_show' || bill.kind === 'late_cancel';
      const detail = [courseLanguageLabel(r.courseLanguage), licenseTypeLabel(r.licenseType)].filter(Boolean).join(' · ');
      return `
        <tr>
          <td>${esc(formatDate(r.date))}<br/><span class="muted">${esc(formatTime(r.startTime))}</span></td>
          <td>${esc(r.userName)}<br/><span class="muted">${esc(r.userPhone || '')}${r.licenseNumber ? ` · Lic. ${esc(r.licenseNumber)}` : ''}</span></td>
          <td>${esc(r.categoryName)}${detail ? `<br/><span class="muted">${esc(detail)}</span>` : ''}</td>
          <td>${esc(st.label)}</td>
          <td class="num">${money(r.partnerPriceCents)}</td>
          <td class="num">${bill.kind === 'course' ? `− ${money(bill.commissionCents)}` : '—'}</td>
          <td class="num">${isPenalty ? money(bill.penaltyCents) : '—'}</td>
          <td class="num${isPenalty ? ' pen' : ''}"><b>${bill.kind === 'upcoming' ? 'Pendiente' : money(bill.billedCents)}</b></td>
        </tr>`;
    }).join('');

  const courseLines = summary.courseLines.length === 0
    ? `<tr><td>Cursos asistidos (0)</td><td class="num">${money(0)}</td></tr>`
    : summary.courseLines.map((l) => `<tr><td>${esc(l.name)} (${l.count} × ${money(l.priceCents)})</td><td class="num">${money(l.subtotalCents)}</td></tr>`).join('');

  return `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8" />
    <title>Factura ${esc(invoiceNumber(partner, monthKey))} — ${esc(partner ? partner.name : '')}</title>
    <style>
      body { font-family: Arial, Helvetica, sans-serif; margin: 32px; color: #222; font-size: 13px; }
      .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; margin-bottom: 18px; }
      .brand h1 { font-size: 18px; margin: 0; }
      .brand div { color: #666; font-size: 12px; }
      .inv { text-align: right; }
      .inv h2 { margin: 0 0 4px; font-size: 20px; letter-spacing: .5px; }
      .box { border: 1px solid #ccc; padding: 10px 12px; margin-bottom: 16px; display: flex; gap: 32px; flex-wrap: wrap; }
      .box div b { display: block; color: #666; font-weight: normal; font-size: 11px; text-transform: uppercase; }
      h3 { font-size: 14px; margin: 18px 0 6px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #999; padding: 5px 7px; text-align: left; vertical-align: top; }
      th { background: #f0f0f0; font-size: 12px; }
      td.num, th.num { text-align: right; white-space: nowrap; }
      td.pen { color: #b00020; }
      .muted { color: #666; font-size: 11px; }
      .summary { width: auto; min-width: 380px; margin-left: auto; margin-top: 12px; }
      .summary tr.total td { font-weight: bold; background: #f7f7f7; font-size: 14px; }
      .terms { margin-top: 18px; color: #555; font-size: 11px; line-height: 1.5; }
      .status { display: inline-block; padding: 2px 8px; border: 1px solid #999; border-radius: 10px; font-size: 11px; }
      @media print { body { margin: 12px; } }
    </style></head><body>
    <div class="head">
      <div class="brand">
        <h1>B &amp; B Target Center</h1>
        <div>Programa de Aliados (armerías)</div>
      </div>
      <div class="inv">
        <h2>FACTURA / ESTADO DE CUENTA</h2>
        <div><b>No.</b> ${esc(invoiceNumber(partner, monthKey))}</div>
        <div><b>Fecha de emisión:</b> ${esc(todayLabel())}</div>
        ${status ? `<div style="margin-top:4px"><span class="status">${esc(status.label)}</span></div>` : ''}
      </div>
    </div>
    <div class="box">
      <div><b>Facturar a</b>${esc(partner ? partner.name : '')}${partner && partner.contactName ? `<br/><span class="muted">Att. ${esc(partner.contactName)}</span>` : ''}</div>
      <div><b>Contacto</b>${esc((partner && partner.phone) || '')}${partner && partner.email ? `<br/><span class="muted">${esc(partner.email)}</span>` : ''}</div>
      <div><b>Periodo</b>${esc(monthLabel(monthKey))}</div>
      <div><b>Vence</b>${esc(formatDueDate(dueDate))}</div>
    </div>

    <h3>Clientes referidos por ${esc(partner ? partner.name : 'la armería')} — ${esc(monthLabel(monthKey))}</h3>
    <table>
      <thead><tr>
        <th>Fecha</th><th>Cliente</th><th>Curso</th><th>Asistencia</th>
        <th class="num">Precio</th><th class="num">Comisión</th><th class="num">Penalidad</th><th class="num">Se factura</th>
      </tr></thead>
      <tbody>${bodyRows}</tbody>
    </table>

    <table class="summary">
      <tbody>
        ${courseLines}
        <tr><td>Menos comisión por referido (${esc(summary.commissionLabel)})</td><td class="num">− ${money(summary.commissionCents)}</td></tr>
        <tr><td>Penalidad por inasistencia (${esc(summary.noShowPenaltyLabel)})</td><td class="num">+ ${money(summary.noShowPenaltyCents)}</td></tr>
        <tr><td>Penalidad por cancelar con menos de ${PARTNER_CHANGE_HOURS} h (${esc(summary.lateCancelPenaltyLabel)})</td><td class="num">+ ${money(summary.lateCancelPenaltyCents)}</td></tr>
        <tr class="total"><td>TOTAL A PAGAR</td><td class="num">${money(summary.totalCents)}</td></tr>
      </tbody>
    </table>

    <div class="terms">
      Se factura cada curso al que el cliente referido asistió, menos la comisión por referido, más una penalidad por cada
      inasistencia o cancelación con menos de ${PARTNER_CHANGE_HOURS} horas (sin comisión). Las cancelaciones a tiempo valen $0.
      Inscritos: ${summary.total} · Asistieron: ${summary.attended} · No se presentaron: ${summary.noShow} · Cancelados: ${summary.cancelled} · Próximos: ${summary.upcoming}.
      El mes cierra el último día y el pago vence ${termDays} día(s) después; se puede pagar por ACH desde el portal de aliados.
    </div>
    <script>window.onload = () => window.print();</script>
  </body></html>`;
}

export function openPartnerInvoice(args) {
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(partnerInvoiceHtml(args));
  w.document.close();
}
